/**
 * Component Registry for Styles Builder
 *
 * This module defines all available preview components that can be rendered
 * in the Styles Builder preview. Each component is a function that takes
 * design tokens and returns HTML string.
 *
 * The same rendered HTML is used for both:
 * 1. Preview iframe (what user sees)
 * 2. Prompt examples (what AI receives)
 */

import type { DesignTokens } from "@/utils/styleResolver";
import type { PageType, StyleType } from "@/store/zustand/stylesBuilderStore";

export interface PreviewComponent {
  type: string;
  name: string;
  description: string;
  render: (tokens: DesignTokens) => string;
}

export interface RenderedComponent {
  type: string;
  name: string;
  html: string;
}

function getCardClass(tokens: DesignTokens): string {
  return tokens.style === "glass" ? "card glass" : "card";
}

function renderCard(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Card</span>
      <div class="${cardClass}">
        <div class="card-image"></div>
        <div class="card-content">
          <h3 class="card-title">Project Overview</h3>
          <p class="card-desc">Track your project progress and milestones. View detailed analytics and team performance metrics.</p>
          <div class="card-actions">
            <button class="btn btn-primary">View Details</button>
            <button class="btn btn-secondary">Share</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderForm(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Form</span>
      <div class="${cardClass}">
        <div class="form-header">
          <h3 class="form-title">Create Account</h3>
          <p class="form-desc">Enter your information to get started</p>
        </div>
        <div class="form-body">
          <div class="form-group">
            <label class="label">Full Name</label>
            <input type="text" class="input" placeholder="John Doe" />
          </div>
          <div class="form-group">
            <label class="label">Email</label>
            <input type="email" class="input" placeholder="john@example.com" />
          </div>
          <div class="form-group">
            <label class="label">Role</label>
            <div class="select">
              <span>Select a role</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>
            </div>
          </div>
          <div class="form-actions">
            <button class="btn btn-primary btn-full">Create Account</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderStats(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Stats</span>
      <div class="stats-grid">
        <div class="${cardClass} stat-card">
          <span class="stat-label">Total Users</span>
          <span class="stat-value">2,420</span>
          <span class="stat-change positive">+12.5%</span>
        </div>
        <div class="${cardClass} stat-card">
          <span class="stat-label">Revenue</span>
          <span class="stat-value">$45.2k</span>
          <span class="stat-change positive">+8.2%</span>
        </div>
        <div class="${cardClass} stat-card">
          <span class="stat-label">Active Now</span>
          <span class="stat-value">573</span>
          <span class="stat-change negative">-2.4%</span>
        </div>
      </div>
    </div>
  `;
}

function renderSettings(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Settings</span>
      <div class="${cardClass}">
        <div class="settings-row">
          <div class="settings-info">
            <span class="settings-label">Dark Mode</span>
            <span class="settings-desc">Enable dark theme</span>
          </div>
          <div class="toggle active"><div class="toggle-dot"></div></div>
        </div>
        <div class="settings-row">
          <div class="settings-info">
            <span class="settings-label">Notifications</span>
            <span class="settings-desc">Receive email alerts</span>
          </div>
          <div class="toggle"><div class="toggle-dot"></div></div>
        </div>
        <div class="settings-row">
          <div class="settings-info">
            <span class="settings-label">Auto-save</span>
            <span class="settings-desc">Save changes automatically</span>
          </div>
          <div class="toggle active"><div class="toggle-dot"></div></div>
        </div>
      </div>
    </div>
  `;
}

function renderButtons(tokens: DesignTokens): string {
  return `
    <div class="section">
      <span class="section-label">Buttons</span>
      <div class="button-showcase">
        <button class="btn btn-primary">Primary</button>
        <button class="btn btn-secondary">Secondary</button>
        <button class="btn btn-ghost">Ghost</button>
        <button class="btn btn-primary btn-sm">Small</button>
        <button class="btn btn-primary btn-lg">Large</button>
      </div>
    </div>
  `;
}

function renderInputs(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Inputs</span>
      <div class="${cardClass}">
        <div class="inputs-showcase">
          <div class="form-group">
            <label class="label">Text Input</label>
            <input type="text" class="input" placeholder="Enter text..." />
          </div>
          <div class="form-group">
            <label class="label">Select</label>
            <div class="select">
              <span>Choose option</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>
            </div>
          </div>
          <div class="form-group">
            <label class="label">Textarea</label>
            <textarea class="input textarea" placeholder="Enter description..."></textarea>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderTable(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Table</span>
      <div class="${cardClass} table-container">
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Project Alpha</td>
              <td><span class="badge badge-success">Active</span></td>
              <td>Jan 15, 2025</td>
            </tr>
            <tr>
              <td>Project Beta</td>
              <td><span class="badge badge-warning">Pending</span></td>
              <td>Jan 12, 2025</td>
            </tr>
            <tr>
              <td>Project Gamma</td>
              <td><span class="badge badge-neutral">Draft</span></td>
              <td>Jan 10, 2025</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function renderNavigation(tokens: DesignTokens): string {
  return `
    <div class="section">
      <span class="section-label">Navigation</span>
      <div class="nav-showcase">
        <nav class="navbar">
          <div class="nav-brand">Brand</div>
          <div class="nav-links">
            <a class="nav-link active">Home</a>
            <a class="nav-link">Products</a>
            <a class="nav-link">About</a>
          </div>
          <button class="btn btn-primary btn-sm">Sign In</button>
        </nav>
      </div>
    </div>
  `;
}

function renderHero(tokens: DesignTokens): string {
  return `
    <div class="section section-wide">
      <span class="section-label">Hero</span>
      <div class="hero">
        <span class="hero-badge">New Release</span>
        <h1 class="hero-title">Build beautiful interfaces with ease</h1>
        <p class="hero-desc">Create stunning, responsive designs that work seamlessly across all devices. Start building today.</p>
        <div class="hero-actions">
          <button class="btn btn-primary btn-lg">Get Started</button>
          <button class="btn btn-secondary btn-lg">Learn More</button>
        </div>
      </div>
    </div>
  `;
}

function renderPricing(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Pricing</span>
      <div class="pricing-grid">
        <div class="${cardClass} pricing-card">
          <span class="pricing-tier">Starter</span>
          <div class="pricing-price">
            <span class="pricing-amount">$9</span>
            <span class="pricing-period">/month</span>
          </div>
          <ul class="pricing-features">
            <li>5 Projects</li>
            <li>Basic Analytics</li>
            <li>Email Support</li>
          </ul>
          <button class="btn btn-secondary btn-full">Choose Plan</button>
        </div>
        <div class="${cardClass} pricing-card pricing-featured">
          <span class="badge badge-primary">Popular</span>
          <span class="pricing-tier">Pro</span>
          <div class="pricing-price">
            <span class="pricing-amount">$29</span>
            <span class="pricing-period">/month</span>
          </div>
          <ul class="pricing-features">
            <li>Unlimited Projects</li>
            <li>Advanced Analytics</li>
            <li>Priority Support</li>
          </ul>
          <button class="btn btn-primary btn-full">Choose Plan</button>
        </div>
      </div>
    </div>
  `;
}

function renderTestimonial(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Testimonial</span>
      <div class="${cardClass} testimonial">
        <blockquote class="testimonial-quote">
          "This tool has completely transformed our workflow. The design system is intuitive and the results are stunning."
        </blockquote>
        <div class="testimonial-author">
          <div class="avatar"></div>
          <div class="testimonial-info">
            <span class="testimonial-name">Sarah Chen</span>
            <span class="testimonial-role">Design Lead at Acme</span>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderAlert(tokens: DesignTokens): string {
  return `
    <div class="section">
      <span class="section-label">Alerts</span>
      <div class="alert-showcase">
        <div class="alert alert-info">
          <svg class="alert-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <span>New features are available. Check out the changelog.</span>
        </div>
        <div class="alert alert-success">
          <svg class="alert-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <span>Your changes have been saved successfully.</span>
        </div>
        <div class="alert alert-warning">
          <svg class="alert-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>Your session will expire in 5 minutes.</span>
        </div>
      </div>
    </div>
  `;
}

function renderTabs(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Tabs</span>
      <div class="${cardClass}">
        <div class="tabs">
          <button class="tab active">Overview</button>
          <button class="tab">Analytics</button>
          <button class="tab">Reports</button>
          <button class="tab">Settings</button>
        </div>
        <div class="tab-content">
          <p>Select a tab to view its content. Tabs help organize related information into separate views.</p>
        </div>
      </div>
    </div>
  `;
}

function renderProgress(tokens: DesignTokens): string {
  const cardClass = getCardClass(tokens);

  return `
    <div class="section">
      <span class="section-label">Progress</span>
      <div class="${cardClass}">
        <div class="progress-showcase">
          <div class="progress-item">
            <div class="progress-header">
              <span class="progress-label">Project completion</span>
              <span class="progress-value">75%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" style="width: 75%"></div>
            </div>
          </div>
          <div class="progress-item">
            <div class="progress-header">
              <span class="progress-label">Storage used</span>
              <span class="progress-value">45%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" style="width: 45%"></div>
            </div>
          </div>
          <div class="progress-item">
            <div class="progress-header">
              <span class="progress-label">Tasks completed</span>
              <span class="progress-value">90%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" style="width: 90%"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderBadges(tokens: DesignTokens): string {
  return `
    <div class="section">
      <span class="section-label">Badges</span>
      <div class="badges-showcase">
        <div class="badge-row">
          <span class="badge badge-primary">Primary</span>
          <span class="badge badge-secondary">Secondary</span>
          <span class="badge badge-success">Success</span>
          <span class="badge badge-warning">Warning</span>
          <span class="badge badge-error">Error</span>
          <span class="badge badge-neutral">Neutral</span>
        </div>
        <div class="badge-row">
          <span class="badge badge-outline-primary">Outline</span>
          <span class="badge badge-outline-secondary">Outline</span>
          <span class="badge badge-outline-success">Outline</span>
        </div>
      </div>
    </div>
  `;
}

export const COMPONENT_REGISTRY: Record<string, PreviewComponent> = {
  card: {
    type: "card",
    name: "Card",
    description: "Content card with image, title, description, and actions",
    render: renderCard,
  },
  form: {
    type: "form",
    name: "Form",
    description: "Form with inputs, labels, and submit button",
    render: renderForm,
  },
  stats: {
    type: "stats",
    name: "Stats",
    description: "Statistics cards with values and change indicators",
    render: renderStats,
  },
  settings: {
    type: "settings",
    name: "Settings",
    description: "Settings panel with toggle switches",
    render: renderSettings,
  },
  buttons: {
    type: "buttons",
    name: "Buttons",
    description: "Button variants and sizes",
    render: renderButtons,
  },
  inputs: {
    type: "inputs",
    name: "Inputs",
    description: "Form input variants",
    render: renderInputs,
  },
  table: {
    type: "table",
    name: "Table",
    description: "Data table with headers and rows",
    render: renderTable,
  },
  navigation: {
    type: "navigation",
    name: "Navigation",
    description: "Navigation bar with links",
    render: renderNavigation,
  },
  hero: {
    type: "hero",
    name: "Hero",
    description: "Hero section with headline, description, and CTAs",
    render: renderHero,
  },
  pricing: {
    type: "pricing",
    name: "Pricing",
    description: "Pricing cards with tiers and features",
    render: renderPricing,
  },
  testimonial: {
    type: "testimonial",
    name: "Testimonial",
    description: "Customer testimonial with quote and author",
    render: renderTestimonial,
  },
  alert: {
    type: "alert",
    name: "Alerts",
    description: "Alert messages with different severity levels",
    render: renderAlert,
  },
  tabs: {
    type: "tabs",
    name: "Tabs",
    description: "Tabbed navigation for content sections",
    render: renderTabs,
  },
  progress: {
    type: "progress",
    name: "Progress",
    description: "Progress bars with labels and values",
    render: renderProgress,
  },
  badges: {
    type: "badges",
    name: "Badges",
    description: "Badge variants for status and labels",
    render: renderBadges,
  },
};

/**
 * Maps page types to the most relevant preview components.
 * Each page type shows components that best demonstrate its use case.
 */
export const PAGE_TYPE_COMPONENTS: Record<PageType, string[]> = {
  landing: ["hero", "navigation", "card", "testimonial", "pricing"],
  dashboard: ["stats", "progress", "table", "tabs", "alert"],
  settings: ["settings", "inputs", "tabs", "alert"],
  onboarding: ["form", "progress", "buttons", "card"],
  ecommerce: ["card", "pricing", "stats", "badges", "buttons"],
  blog: ["card", "navigation", "testimonial", "hero", "badges"],
  docs: ["table", "tabs", "inputs", "navigation", "alert"],
  portfolio: ["hero", "card", "testimonial", "buttons"],
};

/**
 * Maps style types to components that best showcase that style.
 * These are merged with page type components for richer previews.
 */
export const STYLE_COMPONENTS: Record<StyleType, string[]> = {
  minimal: ["card", "buttons", "navigation"],
  bold: ["hero", "badges", "buttons", "alert"],
  soft: ["testimonial", "card", "progress"],
  glass: ["card", "tabs", "pricing", "testimonial"],
  elegant: ["testimonial", "pricing", "hero", "card"],
  corporate: ["stats", "table", "progress", "form"],
};

/**
 * Default components shown when no page type or style is specified.
 */
export const DEFAULT_PREVIEW_COMPONENTS = [
  "hero",
  "card",
  "form",
  "stats",
  "buttons",
  "testimonial",
];

/**
 * Gets the component types for a given page type and style.
 * Merges page type components with style-specific components for richer previews.
 */
export function getComponentsForPageType(
  pageType: PageType | null,
  style?: StyleType | null,
): string[] {
  const pageComponents = pageType
    ? (PAGE_TYPE_COMPONENTS[pageType] ?? [])
    : DEFAULT_PREVIEW_COMPONENTS;

  // If no style, just return page components
  if (!style) {
    return pageComponents;
  }

  // Get style-specific components
  const styleComponents = STYLE_COMPONENTS[style] ?? [];

  // Merge: start with page components, then add style components that aren't already included
  const merged = [...pageComponents];
  for (const comp of styleComponents) {
    if (!merged.includes(comp)) {
      merged.push(comp);
    }
  }

  // Limit to 6 components to avoid overwhelming the preview
  return merged.slice(0, 6);
}

/**
 * Gets preview components for a given list of component types.
 * Falls back to defaults if invalid types are provided.
 */
export function getPreviewComponents(
  componentTypes?: string[],
): PreviewComponent[] {
  const types = componentTypes ?? DEFAULT_PREVIEW_COMPONENTS;
  return types
    .map((type) => COMPONENT_REGISTRY[type])
    .filter(
      (component): component is PreviewComponent => component !== undefined,
    );
}

/**
 * Renders all specified components and returns both HTML and metadata.
 */
export function renderComponents(
  tokens: DesignTokens,
  componentTypes?: string[],
): RenderedComponent[] {
  const components = getPreviewComponents(componentTypes);

  return components.map((component) => ({
    type: component.type,
    name: component.name,
    html: component.render(tokens),
  }));
}

/**
 * Renders all components into a single HTML string for the preview grid.
 */
export function renderComponentsHTML(
  tokens: DesignTokens,
  componentTypes?: string[],
): string {
  const rendered = renderComponents(tokens, componentTypes);
  const componentsHTML = rendered.map((c) => c.html).join("\n");

  return `
    <div class="preview-grid">
      ${componentsHTML}
    </div>
  `;
}
